import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import atlasPlates from '../data/atlas_geo_plates.json';
import { Map as MapIcon, X, ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2, RefreshCw, ArrowLeft, Move } from 'lucide-react';

function toRoman(num) {
  const lookup = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 };
  let roman = '';
  for (let i in lookup) {
    while (num >= lookup[i]) {
      roman += i;
      num -= lookup[i];
    }
  }
  return roman;
}

export default function CartesGeoToc() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  const fromBible = useMemo(() => {
    return Boolean(
      location.state?.fromBible ||
      searchParams.get('from_bible') === '1' ||
      ((searchParams.get('planche') || searchParams.get('Carte')) && document.referrer && !document.referrer.includes('/cartes'))
    );
  }, [location.state, searchParams]);

  // Group plates by section title
  const sections = useMemo(() => {
    const out = [];
    atlasPlates.forEach((p) => {
      const last = out[out.length - 1];
      if (last && last.title === p.title) last.plates.push(p);
      else out.push({ title: p.title || 'Cartes et Plans', plates: [p] });
    });
    return out;
  }, []);

  // Direct open via ?planche=N or ?Carte=N (supports integers or Roman numerals)
  useEffect(() => {
    const raw = searchParams.get('planche') || searchParams.get('Carte');
    if (raw) {
      let n = parseInt(raw, 10);
      if (isNaN(n) || n <= 0) {
        // Simple Roman numeral decoder
        const map = { i: 1, v: 5, x: 10, l: 50, c: 100 };
        const s = String(raw).trim().toLowerCase();
        let total = 0;
        for (let i = 0; i < s.length; i++) {
          const v = map[s[i]];
          if (!v) { total = 0; break; }
          const next = map[s[i + 1]] || 0;
          total += v < next ? -v : v;
        }
        if (total > 0) n = total;
      }
      if (n > 0) {
        const p = atlasPlates.find((x) => x.plate === n);
        if (p) {
          setSelected(p);
          setRotation(0);
          setZoom(1);
        }
      }
    }
  }, [searchParams]);

  const resetTransform = () => {
    setRotation(0);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const openModal = (p) => {
    setSelected(p);
    resetTransform();
  };

  const close = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    if (fromBible) {
      if (location.state?.returnUrl) {
        navigate(location.state.returnUrl);
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/bible');
      }
    } else {
      setSelected(null);
      resetTransform();
      setSearchParams({}, { replace: true });
    }
  };

  const toggleFullscreen = () => {
    if (!modalRef.current) return;
    if (!document.fullscreenElement) {
      modalRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const rotateClockwise = () => {
    setRotation((r) => (r + 90) % 360);
  };

  const zoomIn = () => setZoom((z) => Math.min(+(z + 0.3).toFixed(2), 4));
  const zoomOut = () => setZoom((z) => Math.max(+(z - 0.3).toFixed(2), 0.7));

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoom((z) => Math.min(+(z + 0.15).toFixed(2), 4));
    } else {
      setZoom((z) => Math.max(+(z - 0.15).toFixed(2), 0.7));
    }
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setPan({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  // Écouteurs globaux window pour un drag fluide même si le curseur sort du conteneur ou passe sur d'autres éléments
  useEffect(() => {
    if (!isDragging) return;

    const onWindowMouseMove = (e) => {
      setPan({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y
      });
    };

    const onWindowMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUp);

    return () => {
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
    };
  }, [isDragging]);

  // Support tactile (mobile / tablette / écran tactile)
  const touchStartRef = useRef({ x: 0, y: 0 });
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX - pan.x, y: t.clientY - pan.y };
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    const t = e.touches[0];
    setPan({
      x: t.clientX - touchStartRef.current.x,
      y: t.clientY - touchStartRef.current.y
    });
  };

  const handleTouchEnd = () => setIsDragging(false);

  const handleDoubleClick = () => {
    if (zoom > 1.2) {
      resetTransform();
    } else {
      setZoom(2.2);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8 animate-fade-in">
      <div className="flex items-center space-x-3 border-b border-border pb-4">
        <MapIcon className="h-8 w-8 text-emerald-700" />
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-primary">
            Atlas géographique de la Bible
          </h1>
          <p className="text-xs text-ink-secondary mt-1">L.-Cl. Fillion — Édition 1920</p>
        </div>
      </div>
      <p className="text-ink-secondary text-sm sm:text-base">
        {atlasPlates.length} cartes et plans tirés de l'Atlas géographique de la Sainte Bible. Cliquez sur une carte pour l'agrandir, pivoter ou passer en plein écran.
      </p>

      {sections.map((s, i) => (
        <section key={i} className="space-y-3">
          <h2 className="text-lg font-heading font-semibold text-ink-primary border-b border-border/50 pb-1">
            {s.title}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {s.plates.map((p) => {
              const romanPlate = toRoman(p.plate);
              return (
                <button
                  type="button"
                  key={p.plate}
                  onClick={() => openModal(p)}
                  className="group flex flex-col text-left p-2 rounded-lg bg-paper border border-border hover:border-emerald-600/50 shadow-sm transition-all hover:shadow-md"
                >
                  <div className="w-full h-44 bg-black/5 rounded overflow-hidden flex items-center justify-center relative">
                    <img src={p.thumb || p.src} alt={`Planche ${romanPlate}`} loading="lazy" className="w-full h-full object-contain transition-transform group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <ZoomIn className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <span className="mt-2 text-sm font-semibold text-ink-primary group-hover:text-emerald-700">
                    Planche <span style={{ fontVariant: 'small-caps' }}>{romanPlate}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {selected && (
        <div 
          ref={modalRef}
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-hidden flex flex-col items-center justify-between p-2 sm:p-4" 
          onClick={close}
        >
          {/* Top Bar Controls */}
          <div className="w-full max-w-6xl flex justify-between items-center bg-paper/95 rounded-lg px-4 py-3 shadow-lg z-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              {fromBible ? (
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 text-xs sm:text-sm font-medium transition shadow-xs cursor-pointer"
                  title="Revenir directement au texte biblique"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Retour au texte</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-paper-dark hover:bg-paper-dark/80 text-ink-primary text-xs sm:text-sm font-medium transition border border-border cursor-pointer"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Toutes les cartes</span>
                </button>
              )}
              <div>
                <h3 className="text-lg sm:text-xl font-heading font-bold text-ink-primary">
                  Planche <span style={{ fontVariant: 'small-caps' }}>{toRoman(selected.plate)}</span>
                </h3>
                <p className="text-xs sm:text-sm text-ink-secondary truncate max-w-xs sm:max-w-md">{selected.title}</p>
              </div>
            </div>

            {/* Viewer Controls */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button 
                type="button" 
                onClick={rotateClockwise} 
                title="Pivoter de 90°"
                className="p-2 rounded bg-paper-dark hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-ink-primary transition-colors flex items-center gap-1 text-xs font-medium"
              >
                <RotateCw className="h-4 w-4 text-emerald-700" />
                <span className="hidden sm:inline">Pivoter</span>
              </button>
              <button 
                type="button" 
                onClick={zoomOut} 
                title="Dézoomer -"
                className="p-2 rounded bg-paper-dark hover:bg-emerald-100 text-ink-primary transition-colors"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="text-xs font-mono px-1.5 py-1 text-ink-secondary min-w-[3rem] text-center hidden sm:inline">
                {Math.round(zoom * 100)}%
              </span>
              <button 
                type="button" 
                onClick={zoomIn} 
                title="Zoomer +"
                className="p-2 rounded bg-paper-dark hover:bg-emerald-100 text-ink-primary transition-colors"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              <button 
                type="button" 
                onClick={resetTransform} 
                title="Réinitialiser"
                className="p-2 rounded bg-paper-dark hover:bg-emerald-100 text-ink-primary transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button 
                type="button" 
                onClick={toggleFullscreen} 
                title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                className="p-2 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-medium transition-colors flex items-center gap-1 text-xs"
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                <span className="hidden sm:inline">{isFullscreen ? 'Quitter' : 'Plein écran'}</span>
              </button>
              <button 
                type="button" 
                onClick={close} 
                title="Fermer" 
                className="p-2 rounded-full hover:bg-red-100 text-ink-secondary hover:text-red-700 transition-colors ml-2"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Main Image Stage with Drag & Wheel Zoom */}
          <div 
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onDoubleClick={handleDoubleClick}
            className={`flex-1 w-full relative flex items-center justify-center overflow-hidden select-none p-2 ${
              isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Visual drag helper */}
            <div className="absolute bottom-4 left-4 z-10 hidden sm:flex items-center gap-2 px-2.5 py-1 rounded bg-black/60 backdrop-blur-xs text-white/80 text-[11px] pointer-events-none">
              <Move className="h-3 w-3" />
              <span>Glisser pour déplacer · Molette pour zoomer · Double-clic pour réinitialiser</span>
            </div>

            <div 
              className={`flex items-center justify-center max-w-full max-h-full ${isDragging ? '' : 'transition-transform duration-150 ease-out'}`}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${zoom})`,
                transformOrigin: 'center center'
              }}
            >
              <img 
                src={selected.src} 
                alt={`Planche ${toRoman(selected.plate)}`} 
                draggable={false}
                className="max-w-[90vw] max-h-[80vh] object-contain rounded shadow-2xl bg-white/5 pointer-events-none select-none" 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
